import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, User, Building2, Calendar, DollarSign, AlertCircle, FileText, Upload, Eye, Download } from "lucide-react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import ReceiptUpload from "./ReceiptUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatCurrency } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { resolveFileUrl, downloadFile } from "@/api/base44Client";

const paymentMethodConfig = {
  Cash: { label: 'Cash', color: 'bg-green-100 text-green-700 border-green-200' },
  Installment: { label: 'Installment', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  // Keep lowercase for backward compatibility if needed, or migration
  cash: { label: 'Cash', color: 'bg-green-100 text-green-700 border-green-200' },
};

const paymentTypeConfig = {
  first_installment: { label: 'Advanced Payment', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  monthly: { label: 'Monthly', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  additional: { label: 'Additional', color: 'bg-pink-100 text-pink-700 border-pink-200' },
  final: { label: 'Final Payment', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

export default function PaymentCard({ payment, sale, client, apartment, receipts, delayInfo, onEdit, onDelete }) {
  const { canEdit, canExport } = useAuth();
  const [showReceiptUpload, setShowReceiptUpload] = useState(false);
  const [showReceiptView, setShowReceiptView] = useState(false);
  const [receiptDisplayUrl, setReceiptDisplayUrl] = useState(null);
  const [receiptLoadError, setReceiptLoadError] = useState(null);
  const hasReceipt = receipts && receipts.length > 0;

  // Use direct URL for receipt when viewing (avoids fetch/CORS issues)
  useEffect(() => {
    if (!showReceiptView || !hasReceipt) {
      if (!showReceiptView) {
        setReceiptDisplayUrl(null);
        setReceiptLoadError(null);
      }
      return;
    }
    const rawUrl = receipts[0].file_url || receipts[0].receipt_url;
    if (!rawUrl) {
      setReceiptDisplayUrl(null);
      setReceiptLoadError('No receipt file');
      return;
    }
    if (rawUrl.startsWith('data:')) {
      setReceiptDisplayUrl(rawUrl);
      setReceiptLoadError(null);
      return;
    }
    setReceiptDisplayUrl(resolveFileUrl(rawUrl));
    setReceiptLoadError(null);
  }, [showReceiptView, hasReceipt, receipts?.[0]?.file_url, receipts?.[0]?.receipt_url]);

  // delayInfo now reflects whether the SALE is currently overdue (same
  // definition as the Sales tab), not whether this specific payment was
  // historically made after its own due date.
  const isDelayed = delayInfo?.isDelayed ?? false;
  const missedPayments = delayInfo?.missedPayments ?? 0;

  const method = paymentMethodConfig[payment.payment_method] || paymentMethodConfig.cash;
  const type = paymentTypeConfig[payment.payment_type] || null;
  const receiptStatus = hasReceipt ? receipts[0].status : null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card className={`hover:shadow-lg transition-all duration-300 border-0 shadow-md ${
          isDelayed ? 'border-l-4 border-l-red-500' : ''
        }`}>
          <CardContent className="p-6">
            <div className="space-y-4">
              {/* Header */}
              <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className="w-14 h-14 bg-gradient-to-br from-slate-900 to-slate-700 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-7 h-7 text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold text-slate-900">
                    {formatCurrency(payment.amount, payment.currency)}
                    </h3>
                      <Badge className={`${method.color} border`}>
                        {method.label}
                      </Badge>
                      {type && (
                        <Badge className={`${type.color} border`}>
                          {type.label}
                        </Badge>
                      )}
                      {isDelayed && (
                      <Badge className="bg-red-100 text-red-700 border-red-200 border flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Overdue{missedPayments > 0 ? ` - ${missedPayments} missed` : ''}
                      </Badge>
                    )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span className="font-medium">{client?.full_name || 'Unknown Client'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        <span>Unit {apartment?.unit_number || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{format(new Date(payment.payment_date), 'MMM d, yyyy')}</span>
                      </div>
                    </div>
                  </div>
                </div>
                {canEdit && (
                <div className="flex gap-2">
                  <Button variant="outline" size="icon" onClick={() => onEdit(payment)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => onDelete(payment)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
                )}
              </div>

              {/* Payment Details */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t">
                {payment.reference_number && (
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Reference #</p>
                    <p className="font-semibold text-slate-900 text-sm">{payment.reference_number}</p>
                  </div>
                )}
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Payment Method</p>
                  <p className="font-semibold text-slate-900 text-sm">{method.label}</p>
                </div>
                {payment.payment_type && (
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Payment Type</p>
                    <p className="font-semibold text-slate-900 text-sm">
                      {payment.payment_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                  </div>
                )}
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Receipt Status</p>
                  {hasReceipt ? (
                    <Badge className={receiptStatus === 'available' 
                      ? 'bg-green-100 text-green-700 border-green-200 border' 
                      : 'bg-amber-100 text-amber-700 border-amber-200 border'
                    }>
                      {receiptStatus === 'available' ? 'Available' : 'Pending'}
                    </Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-700 border-gray-200 border">
                      No Receipt
                    </Badge>
                  )}
                </div>
              </div>

              {/* Receipt Actions */}
              <div className="flex flex-wrap gap-3 pt-3 border-t">
                {hasReceipt ? (
                  <>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="gap-2"
                      onClick={() => setShowReceiptView(true)}
                    >
                      <Eye className="w-4 h-4" />
                      View Receipt
                    </Button>
                    {canEdit && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="gap-2"
                        onClick={() => setShowReceiptUpload(true)}
                      >
                        <Upload className="w-4 h-4" />
                        Update Receipt
                      </Button>
                    )}
                  </>
                ) : (
                  canEdit && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="gap-2"
                      onClick={() => setShowReceiptUpload(true)}
                    >
                      <Upload className="w-4 h-4" />
                      Upload Receipt
                    </Button>
                  )
                )}
              </div>

              {/* Notes */}
              {payment.notes && (
                <div className="pt-3 border-t">
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-slate-400 mt-1" />
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Notes</p>
                      <p className="text-sm text-slate-700">{payment.notes}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Receipt Upload Dialog */}
      <Dialog open={showReceiptUpload} onOpenChange={setShowReceiptUpload}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {hasReceipt ? 'Update Payment Receipt' : 'Upload Payment Receipt'}
            </DialogTitle>
          </DialogHeader>
          <ReceiptUpload
            payment={payment}
            existingReceipt={hasReceipt ? receipts[0] : null}
            onClose={() => setShowReceiptUpload(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Receipt View Dialog */}
      {hasReceipt && (
        <Dialog open={showReceiptView} onOpenChange={setShowReceiptView}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>Payment Receipt</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 overflow-y-auto max-h-[calc(90vh-140px)] pr-2">
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Upload Date</p>
                  <p className="font-semibold text-slate-900">
                    {format(new Date(receipts[0].upload_date || receipts[0].created_date), 'PPP')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Status</p>
                  <Badge className={receiptStatus === 'available' 
                    ? 'bg-green-100 text-green-700 border-green-200 border' 
                    : 'bg-amber-100 text-amber-700 border-amber-200 border'
                  }>
                    {receiptStatus === 'available' ? 'Available' : 'Pending'}
                  </Badge>
                </div>
              </div>
              <div className="border rounded-lg overflow-hidden bg-white min-h-[300px]">
                {receiptLoadError ? (
                  <div className="p-8 text-center text-slate-600">
                    <p className="font-medium">{receiptLoadError}</p>
                    <p className="text-sm mt-2">The file may have been removed or the server was restarted.</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={() => {
                        const rawUrl = receipts[0].file_url || receipts[0].receipt_url;
                        if (rawUrl && !rawUrl.startsWith('data:')) {
                          window.open(resolveFileUrl(rawUrl), '_blank', 'noopener,noreferrer');
                        }
                      }}
                    >
                      Try opening in new tab
                    </Button>
                  </div>
                ) : receiptDisplayUrl ? (
                  receipts[0].file_type === 'pdf' ? (
                    <iframe 
                      src={receiptDisplayUrl} 
                      className="w-full min-h-[500px]"
                      title="Receipt PDF"
                    />
                  ) : (
                    <img
                      src={receiptDisplayUrl}
                      alt="Receipt"
                      className="w-full h-auto max-w-full"
                      onError={() => {
                        setReceiptDisplayUrl(null);
                        setReceiptLoadError('Failed to load');
                      }}
                    />
                  )
                ) : (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto" />
                    <p className="text-sm text-slate-500 mt-2">Loading receipt...</p>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 pb-2">
                <Button variant="outline" onClick={() => setShowReceiptView(false)}>
                  Close
                </Button>
                {canExport && (
                <Button
                  type="button"
                  onClick={async () => {
                    const rawUrl = receipts[0].file_url || receipts[0].receipt_url;
                    if (!rawUrl) return;
                    const url = resolveFileUrl(rawUrl);
                    const ext = receipts[0].file_type === 'pdf' ? 'pdf' : 'png';
                    const filename = `receipt-${payment.id || 'payment'}-${Date.now()}.${ext}`;
                    await downloadFile(url, filename);
                  }}
                  className="bg-gradient-to-r from-slate-900 to-slate-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}